import React from "react";
import { Import, Edit, Edit2, CloseCircle } from "iconsax-react";
import { CheckIcon, CloseIcon } from "@/shared/assets/icons";
import { PERMISSIONS } from "@/shared/consts";
import type { ResignationLetterResponse, ResignationStatus } from "../types";

export type ResignationAction =
  | "approve"
  | "reject"
  | "request_revision"
  | "revoke"
  | "sign_order"
  | "download_order_docx"
  | "edit"
  | "cancel"
  | "delete";

export interface ActionConfig {
  id: ResignationAction;
  label: string;
  variant: "primary" | "secondary" | "danger";
  icon: React.ReactNode;
}

export interface ResignationActionsConfig {
  actions: ActionConfig[];
  isLoading: boolean;
}

interface IsLoadingState {
  isDownloadingOrder: boolean;
  isCancelling: boolean;
  isDeleting: boolean;
  isRevisioning: boolean;
}

export function getAvailableActions(
  resignation: ResignationLetterResponse,
  isLoading: IsLoadingState,
  t: (key: string) => string,
): ResignationActionsConfig {
  const status = resignation.status as ResignationStatus;
  const actions: ActionConfig[] = [];

  const isDraft = status === "draft";
  const hasRevisionNote = resignation.application_review_note != null && resignation.application_review_note !== "";
  const canApproveDraft = isDraft && !hasRevisionNote;
  const canRejectDraft = isDraft && !hasRevisionNote;
  const canRequestRevision = isDraft && !hasRevisionNote;
  const isSigningPending = status === "signing_pending";
  const canSignOrder = status === "order_pending";
  const canDownloadOrderDocx =
    (status === "order_pending" || status === "order_uploaded" || status === "completed") &&
    resignation.order &&
    !resignation.order.signed_pdf_url;

  if (canApproveDraft) {
    actions.push({
      id: "approve",
      label: t("resignationLetter.actions.approve"),
      variant: "primary",
      icon: React.createElement(CheckIcon),
    });
  }

  if (canRequestRevision) {
    actions.push({
      id: "request_revision",
      label: t("resignationLetter.actions.requestRevision"),
      variant: "secondary",
      icon: React.createElement(Edit2, { size: 16, color: "currentColor" }),
    });
  }

  if (canRejectDraft) {
    actions.push({
      id: "reject",
      label: t("resignationLetter.actions.reject"),
      variant: "danger",
      icon: React.createElement(CloseIcon),
    });
  }

  if (isSigningPending) {
    actions.push({
      id: "revoke",
      label: t("resignationLetter.actions.revoke"),
      variant: "danger",
      icon: React.createElement(CloseCircle, { size: 16, color: "currentColor" }),
    });
  }

  if (canSignOrder) {
    actions.push({
      id: "sign_order",
      label: t("resignationLetter.actions.signOrder"),
      variant: "primary",
      icon: React.createElement(Edit, { size: 16, color: "currentColor" }),
    });
  }

  if (canDownloadOrderDocx) {
    actions.push({
      id: "download_order_docx",
      label: isLoading.isDownloadingOrder
        ? t("resignationLetter.actions.downloading")
        : t("resignationLetter.actions.downloadOrderDocx"),
      variant: "secondary",
      icon: React.createElement(Import, { size: 16, color: "currentColor" }),
    });
  }


  const isLoadingAny =
    isLoading.isDownloadingOrder ||
    isLoading.isCancelling ||
    isLoading.isDeleting ||
    isLoading.isRevisioning;

  return {
    actions,
    isLoading: isLoadingAny,
  };
}

export function filterActionsByPermissions(
  actions: ActionConfig[],
  hasPermission: (p: string) => boolean,
  hasRole: (r: string) => boolean,
): ActionConfig[] {
  return actions.filter(function filterByPermission(action) {
    if (action.id === "approve" || action.id === "sign_order") {
      return hasPermission(PERMISSIONS.HR.EDIT_EMPLOYEE) && hasRole("chief");
    }
    if (action.id === "reject" || action.id === "request_revision" || action.id === "revoke") {
      return hasPermission(PERMISSIONS.HR.EDIT_EMPLOYEE);
    }
    return true;
  });
}
